import classNames from 'classnames';
import { type ComponentType, lazy, type ReactNode, Suspense } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import { Loading } from '@revelio/core';
import { Button } from '@revelio/ui';

import styles from './lazy-route.module.css';

const RELOADED_KEY = 'lazy-route-reloaded';
// Covers the reload itself, so a chunk still failing after it isn't reloaded again;
// a stale deploy hit later in the same tab still gets its reload.
const RELOAD_WINDOW_MS = 5 * 60_000;

const isChunkLoadError = (error: unknown) =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    (error as { code?: unknown }).code === 'CSS_CHUNK_LOAD_FAILED');

// sessionStorage throws where storage is blocked (Safari private mode, embedded
// webviews); without it a reload can't be bounded, so none is attempted.
const markReload = () => {
  try {
    const reloadedAt = Number(window.sessionStorage.getItem(RELOADED_KEY));
    if (Date.now() - reloadedAt < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

const clearReload = () => {
  try {
    window.sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    // Storage blocked: nothing was marked.
  }
};

// React.lazy with stale-deploy recovery: reload once on a failed chunk load, then surface to LazyRoute.
export function lazyWithReload<P>(
  factory: () => Promise<{ default: ComponentType<P> }>
) {
  return lazy(() =>
    factory()
      .then((module) => {
        clearReload();
        return module;
      })
      .catch((error: unknown) => {
        if (isChunkLoadError(error) && markReload()) {
          window.location.reload();
          // never resolves: keeps the Suspense fallback up during reload
          return new Promise<{ default: ComponentType<P> }>(() => {});
        }
        throw error;
      })
  );
}

export function lazyNamedWithReload<P, K extends PropertyKey>(
  factory: () => Promise<Record<K, ComponentType<P>>>,
  name: K
) {
  return lazyWithReload<P>(() =>
    factory().then((module) => ({ default: module[name] }))
  );
}

const LazyRouteError = ({ error }: FallbackProps) => {
  if (!isChunkLoadError(error)) throw error;
  return (
    <div className={styles.fallback} data-testid="lazy-route-error">
      <p className={styles.message}>This page failed to load.</p>
      <Button color="primary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
};

const LazyRouteLoading = () => (
  <div className={classNames(styles.fallback, styles.delayed)}>
    <Loading />
  </div>
);

export const LazyRoute = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary FallbackComponent={LazyRouteError} resetKeys={[pathname]}>
      <Suspense fallback={<LazyRouteLoading />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

export const lazyRouteElement = (Page: ComponentType) => (
  <LazyRoute>
    <Page />
  </LazyRoute>
);
